import { Button, Card, message, Table, Tag, Typography, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';

import { formatMNT, queryErrorMessage } from '../CashTransactions/types';
import CashBatchDetail from './CashBatchDetail';
import type { BatchRow, BatchStatus } from './types';
import { STATUS_TAGS } from './types';

const { Title } = Typography;

const TEMPLATE =
  'student_code,full_name,amount,type,description\nST0561,Э. Эрдэмбаян,10000,Орлого,Өмнөх жилийн картын үлдэгдэл\n';

// BOM so Excel opens the template's Cyrillic as UTF-8.
function downloadTemplate() {
  const blob = new Blob([`﻿${TEMPLATE}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'bagts-guilgee.csv';
  link.click();
  URL.revokeObjectURL(url);
}

// "Багц гүйлгээ" (CONTEXT.md: Batch transaction): upload a CSV, review its
// checked rows, then record them all at once or discard the batch.
export default function CashBatches() {
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';

  const [rows, setRows] = useState<BatchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<{ results: BatchRow[]; total: number }>('cash_batches', { page, pageSize })
      .then(([row]) => {
        setRows(row?.results ?? []);
        setTotal(row?.total ?? 0);
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, page, pageSize]);

  useEffect(() => {
    if (selectedId == null) load();
  }, [load, selectedId]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      // Decoded as UTF-8; the server rejects a file that wasn't.
      const content = await file.text();
      const [row] = await runQuery<{ id: number }>('cash_batch_create', {
        fileName: file.name,
        content,
      });
      if (row) setSelectedId(row.id);
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  if (selectedId != null) {
    return (
      <LayoutWithMenu className="px-3 lg:px-5">
        <CashBatchDetail
          batchId={selectedId}
          canEdit={canEdit}
          onBack={() => setSelectedId(null)}
        />
      </LayoutWithMenu>
    );
  }

  const columns: ColumnsType<BatchRow> = [
    { title: '#', dataIndex: 'id', width: 70 },
    {
      title: 'Огноо',
      dataIndex: 'created_at',
      width: 150,
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—'),
    },
    { title: 'Файл', dataIndex: 'file_name' },
    {
      title: 'Төлөв',
      dataIndex: 'status',
      render: (value: BatchStatus) => (
        <Tag color={STATUS_TAGS[value]?.color}>{STATUS_TAGS[value]?.label ?? value}</Tag>
      ),
    },
    {
      title: 'Мөр',
      dataIndex: 'total_record',
      align: 'right',
      render: (value: number, row) =>
        row.error_record > 0 ? (
          <span>
            {value} <Tag color="red">{row.error_record} алдаатай</Tag>
          </span>
        ) : (
          value
        ),
    },
    { title: 'Орлого', dataIndex: 'deposit_total', align: 'right', render: formatMNT },
    { title: 'Зарлага', dataIndex: 'withdrawal_total', align: 'right', render: formatMNT },
    {
      title: 'Оруулсан',
      dataIndex: 'created_by_name',
      render: (value: string | null) => value ?? '—',
    },
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Багц гүйлгээ
          </Title>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={downloadTemplate}>Жишээ CSV татах</Button>
            {canEdit && (
              <Upload
                accept=".csv,text/csv"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleUpload(file);
                  return false;
                }}
              >
                <Button type="primary" loading={uploading}>
                  CSV оруулах
                </Button>
              </Upload>
            )}
          </div>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: true }}
          onRow={(row) => ({ onClick: () => setSelectedId(row.id), style: { cursor: 'pointer' } })}
          pagination={{
            current: page + 1,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, size) => {
              setPage(size !== pageSize ? 0 : p - 1);
              setPageSize(size);
            },
          }}
        />
      </Card>
    </LayoutWithMenu>
  );
}
